import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Button, Card, Checkbox, Col, Descriptions, Form, InputNumber, Row, Select, Space, Switch, Table, Tag, Typography } from "antd"
import {
  CutKeptInterval,
  GAMEPLAY_CUT_CLASSES,
  GameplayCutClass,
  GameplayCutPreviewBody,
  GameplayCutPreviewConfig,
  GameplayCutPreviewPartners,
  GameplayCutPreviewRecording,
} from "../store/types"
import {
  GAMEPLAY_CUT_CLASS_LABELS,
  GameplayCutFormValues,
  MAX_PREVIEW_RECORDINGS,
  formToGameplayCut,
  resolveGameplayCut,
} from "../gameplay-cut"
import { fmtClock, fmtMinutes } from "../utils"
import { GameplayCutFields } from "./gameplay-cut-fields"
import { RecordingIdsInput } from "./recording-ids-input"

const renderMinuteTags = (entries: Array<[string, number]>) => {
  const nonZero = entries.filter(([, ms]) => ms > 0)

  return nonZero.length ? nonZero.map(([label, ms]) => <Tag key={label}>{label}: {fmtMinutes(ms)}</Tag>) : "—"
}

const PARTNER_OPTIONS: Array<{ value: GameplayCutPreviewPartners; label: string }> = [
  { value: "targets_only", label: "Co-play partners: pasted recordings only" },
  { value: "any_member", label: "Co-play partners: any session member" },
]

const ConfigSummary: FC<{ config: GameplayCutPreviewConfig; partners?: GameplayCutPreviewPartners }> = ({
  config,
  partners,
}) => (
  <Descriptions size="small" column={4} bordered>
    <Descriptions.Item label="Profile">{config.profile}</Descriptions.Item>
    {partners && <Descriptions.Item label="Partners">{partners}</Descriptions.Item>}
    <Descriptions.Item label="Output">{config.outputMode}</Descriptions.Item>
    <Descriptions.Item label="Pipeline">{config.pipelineVersion ?? "all"}</Descriptions.Item>
    <Descriptions.Item label="Kept classes">
      {GAMEPLAY_CUT_CLASSES.filter(c => config.keep?.[c]).map(c => <Tag key={c}>{GAMEPLAY_CUT_CLASS_LABELS[c]}</Tag>)}
    </Descriptions.Item>
    <Descriptions.Item label="Idle threshold">{config.idleThresholdSec}s</Descriptions.Item>
    <Descriptions.Item label="Short break">{config.shortBreakSec === null ? "off" : `${config.shortBreakSec}s`}</Descriptions.Item>
    <Descriptions.Item label="Min block">{config.minBlockSec}s</Descriptions.Item>
    <Descriptions.Item label="Co-play">{config.coplayMinPlayers}+ players, {config.coplayMinSec}s</Descriptions.Item>
  </Descriptions>
)

const IntervalsTable: FC<{ intervals: CutKeptInterval[] }> = ({ intervals }) => (
  <Table
    size="small"
    rowKey={r => `${r.ptsStartMs}-${r.ptsEndMs}`}
    pagination={false}
    dataSource={intervals}
    columns={[
      { title: "Start", key: "start", render: (_: unknown, r: CutKeptInterval) => fmtClock(r.ptsStartMs) },
      { title: "End", key: "end", render: (_: unknown, r: CutKeptInterval) => fmtClock(r.ptsEndMs) },
      { title: "Billed", key: "billed", render: (_: unknown, r: CutKeptInterval) => fmtMinutes(r.billedMs) },
      {
        title: "Pieces",
        key: "pieces",
        render: (_: unknown, r: CutKeptInterval) => r.pieces
          .map(p => `${fmtClock(p.ptsStartMs)}–${fmtClock(p.ptsEndMs)}`)
          .join(", "),
      },
    ]}
  />
)

export const CutPreview: FC = observer(() => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const [form] = Form.useForm<GameplayCutFormValues>()
  const [recordingIds, setRecordingIds] = useState<string[]>([])
  const [contractId, setContractId] = useState<string | undefined>()
  const [custom, setCustom] = useState(false)
  const [pipelineVersion, setPipelineVersion] = useState<number | null>(null)
  const [unpinned, setUnpinned] = useState(false)
  const [partners, setPartners] = useState<GameplayCutPreviewPartners>("targets_only")
  const [running, setRunning] = useState(false)

  useEffect(() => {
    store.loadContracts()
  }, [store])

  const contract = store.contracts.find(c => c.id === contractId)

  useEffect(() => {
    if (custom) form.setFieldsValue(resolveGameplayCut(contract?.gameplayCut))
  }, [form, contract, custom])

  const tooMany = recordingIds.length > MAX_PREVIEW_RECORDINGS

  const onRun = async() => {
    const body: GameplayCutPreviewBody = { recordingIds, contractId }
    if (partners !== "targets_only") body.partners = partners
    if (custom) {
      try {
        body.gameplayCut = formToGameplayCut(await form.validateFields())
      } catch {
        return
      }
    } else if (!contract?.gameplayCut) {
      body.gameplayCut = {}
    }
    if (unpinned) body.pipelineVersion = null
    else if (pipelineVersion != null) body.pipelineVersion = pipelineVersion
    setRunning(true)
    await store.previewCut(body)
    setRunning(false)
  }

  const preview = store.cutPreview

  const foundCell = (r: GameplayCutPreviewRecording) => ({ colSpan: r.found ? 1 : 0 })
  const hasUnappliedRules = !!preview?.recordings.some(r => r.unappliedRules?.length)

  const metricColumns = [
    {
      title: "Recorded",
      key: "recorded",
      render: (_: unknown, r: GameplayCutPreviewRecording) => fmtMinutes(r.recordedMs ?? 0),
    },
    {
      title: "Kept",
      key: "kept",
      render: (_: unknown, r: GameplayCutPreviewRecording) => (
        <span>
          <b>{fmtMinutes(r.keptMs ?? 0)}</b>
          {!!r.recordedMs && (
            <Typography.Text type="secondary"> ({Math.round(((r.keptMs ?? 0) / r.recordedMs) * 100)}%)</Typography.Text>
          )}
        </span>
      ),
    },
    { title: "Kept blocks", key: "blockCount", render: (_: unknown, r: GameplayCutPreviewRecording) => r.blockCount ?? 0 },
    { title: "Pieces", key: "pieceCount", render: (_: unknown, r: GameplayCutPreviewRecording) => r.pieceCount ?? 0 },
    {
      title: "Joined breaks",
      key: "joinedBreak",
      render: (_: unknown, r: GameplayCutPreviewRecording) => fmtMinutes(r.joinedBreakMs ?? 0),
    },
    {
      title: "Cut by class",
      key: "cutMsByClass",
      render: (_: unknown, r: GameplayCutPreviewRecording) => renderMinuteTags(
        GAMEPLAY_CUT_CLASSES.map(c => [GAMEPLAY_CUT_CLASS_LABELS[c], r.cutMsByClass?.[c] ?? 0]),
      ),
    },
    {
      title: "Outside game",
      key: "outsideGame",
      render: (_: unknown, r: GameplayCutPreviewRecording) => renderMinuteTags([
        ["idle", r.outsideGameMsByCause?.idle ?? 0],
        ["unfocused", r.outsideGameMsByCause?.unfocused ?? 0],
      ]),
    },
    {
      title: "Voided",
      key: "voided",
      render: (_: unknown, r: GameplayCutPreviewRecording) => (r.voidReason
        ? <Space size={4}>{fmtMinutes(r.voidedMs ?? 0)}<Tag color="red">{r.voidReason}</Tag></Space>
        : "—"),
    },
    {
      title: "Dropped < min",
      key: "dropped",
      render: (_: unknown, r: GameplayCutPreviewRecording) => fmtMinutes(r.droppedUnderMinMs ?? 0),
    },
    {
      title: "Not co-play",
      key: "notCoplay",
      render: (_: unknown, r: GameplayCutPreviewRecording) => fmtMinutes(r.notCoplayMs ?? 0),
    },
    {
      title: "Unavailable",
      key: "unavailable",
      render: (_: unknown, r: GameplayCutPreviewRecording) => (
        <Space size={2} wrap>
          {(r.unavailableClasses ?? []).map((c: GameplayCutClass) => (
            <Tag key={c} color="orange">{GAMEPLAY_CUT_CLASS_LABELS[c] ?? c}</Tag>
          ))}
          {r.idleKnown === false && <Tag color="orange">idle unknown</Tag>}
        </Space>
      ),
    },
    ...(hasUnappliedRules
      ? [{
        title: "Unapplied rules",
        key: "unappliedRules",
        render: (_: unknown, r: GameplayCutPreviewRecording) => (
          <Space size={2} wrap>
            {(r.unappliedRules ?? []).map(rule => <Tag key={rule} color="gold">{rule}</Tag>)}
          </Space>
        ),
      }]
      : []),
  ].map(column => ({ ...column, onCell: foundCell }))

  const columns = [
    {
      title: "Recording",
      key: "recordingId",
      onCell: (r: GameplayCutPreviewRecording) => ({ colSpan: r.found ? 1 : metricColumns.length + 1 }),
      render: (_: unknown, r: GameplayCutPreviewRecording) => (
        <Space size={8}>
          <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.recordingId}</span>
          {!r.found && <Tag color="red">not found</Tag>}
        </Space>
      ),
    },
    ...metricColumns,
  ]

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Card
        title="Preview gameplay cut"
        extra={(
          <Button type="primary" loading={running} disabled={!recordingIds.length || tooMany} onClick={onRun}>
            Run preview
          </Button>
        )}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <RecordingIdsInput
            max={MAX_PREVIEW_RECORDINGS}
            placeholder={`Recording ids (up to ${MAX_PREVIEW_RECORDINGS}), separated by commas or new lines`}
            onChange={parsed => setRecordingIds(parsed.ids)}
          />
          <Row gutter={16} align="middle">
            <Col span={10}>
              <Select
                aria-label="contract"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Contract (optional)"
                style={{ width: "100%" }}
                value={contractId}
                onChange={(id?: string) => setContractId(id)}
                options={store.contracts.map(c => ({
                  value: c.id,
                  label: `${c.name}${c.gameplayCut ? "" : " (no gameplay cut)"}`,
                }))}
              />
            </Col>
            <Col span={6}>
              <Space>
                <InputNumber
                  min={1}
                  precision={0}
                  disabled={unpinned}
                  placeholder={contractId ? "contract pipeline" : "pipeline version"}
                  value={pipelineVersion}
                  onChange={setPipelineVersion}
                />
                <Checkbox checked={unpinned} onChange={e => setUnpinned(e.target.checked)}>Unpinned</Checkbox>
              </Space>
            </Col>
            <Col span={8}>
              <Space>
                <Switch checked={custom} onChange={setCustom} aria-label="custom config" />
                <span>{contractId ? "Override the contract's config" : "Use a custom config"}</span>
              </Space>
            </Col>
          </Row>
          <Select
            aria-label="partners"
            style={{ width: 360 }}
            value={partners}
            onChange={setPartners}
            options={PARTNER_OPTIONS}
          />
          {custom && (
            <Form form={form} layout="vertical" size="small">
              <GameplayCutFields form={form} />
            </Form>
          )}
        </Space>
      </Card>

      {preview && (
        <Card size="small" title="Result">
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <ConfigSummary config={preview.config} partners={preview.partners} />
            <Table
              size="small"
              rowKey="recordingId"
              dataSource={preview.recordings}
              columns={columns}
              pagination={false}
              expandable={{
                rowExpandable: r => !!r.keptIntervals?.length,
                expandedRowRender: r => <IntervalsTable intervals={r.keptIntervals ?? []} />,
              }}
            />
          </Space>
        </Card>
      )}
    </Space>
  )
})
